import { useState, useCallback } from "react";
import { toast } from "sonner";
import { PhotoUploadDrawer } from "@/components/photo-upload-drawer";
import {
  useUploadCalibrationPhoto,
  useDeleteCalibrationPhoto,
} from "@/api/hooks";
import { asPhotoId, type BuildId, type CalibrationId, type PhotoCategoryId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type PhotoResponse = components["schemas"]["PhotoResponse"];

interface CalibrationPhotoDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buildId: BuildId;
  calId: CalibrationId;
  categoryId: PhotoCategoryId;
  photo: PhotoResponse | null | undefined;
  onLoadingChange?: (loading: boolean) => void;
}

export function CalibrationPhotoDrawer({
  open,
  onOpenChange,
  buildId,
  calId,
  categoryId,
  photo,
  onLoadingChange,
}: CalibrationPhotoDrawerProps) {
  const [uploadProgress, setUploadProgress] = useState(0);
  const handleProgress = useCallback((progress: number) => {
    setUploadProgress(progress);
  }, []);
  const upload = useUploadCalibrationPhoto(buildId, calId, handleProgress);
  const remove = useDeleteCalibrationPhoto(buildId);
  const [justUploaded, setJustUploaded] = useState(false);

  const handleFile = useCallback((file: File) => {
    setJustUploaded(false);
    setUploadProgress(0);
    onLoadingChange?.(true);
    upload.mutate(
      { file, category_id: categoryId },
      {
        onSuccess: () => {
          onLoadingChange?.(false);
          setJustUploaded(true);
          setTimeout(() => {
            setJustUploaded(false);
            onOpenChange(false);
          }, 800);
        },
        onError: (err) => {
          toast.error(err.message);
          onLoadingChange?.(false);
        },
      },
    );
  }, [upload, categoryId, onOpenChange, onLoadingChange]);

  const handleDelete = useCallback(() => {
    if (!photo) return;
    remove.mutate(asPhotoId(photo.id), {
      onSuccess: () => {
        toast.success("Photo deleted");
        onOpenChange(false);
      },
      onError: (err) => toast.error(err.message),
    });
  }, [photo, remove, onOpenChange]);

  return (
    <PhotoUploadDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Calibration Photo"
      photoUrl={photo?.image_url}
      isUploading={upload.isPending}
      uploadProgress={uploadProgress}
      isDeleting={remove.isPending}
      justUploaded={justUploaded}
      hasPhoto={!!photo}
      onFileSelected={handleFile}
      onDelete={handleDelete}
    />
  );
}
